import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@core/notifications';
import { AuthService } from '@services/auth';
import { catchError, throwError } from 'rxjs';

const SKIP_UNAUTHORIZED_PATHS = [
  '/auth/login',
  '/auth/register',
  '/admin/admin-login',
];

let isHandlingUnauthorized = false;
let isHandlingForbidden = false;

export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const router = inject(Router);
  const notify = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (shouldSkipUnauthorizedHandling(req.url)) {
        return throwError(() => error);
      }

      if (error.status === 401 && !isHandlingUnauthorized) {
        isHandlingUnauthorized = true;
        const loginUrl = resolveLoginUrl(req.url, router.url);
        injector.get(AuthService).clearSession();
        notify.warning('Your session has expired. Please sign in again.', {
          title: 'Session expired',
        });
        void router.navigateByUrl(loginUrl, { replaceUrl: true }).finally(() => {
          isHandlingUnauthorized = false;
        });
      }

      if (error.status === 403 && !isHandlingForbidden) {
        isHandlingForbidden = true;
        notify.warning(resolveForbiddenMessage(error), {
          title: 'Access denied',
        });
        window.setTimeout(() => {
          isHandlingForbidden = false;
        }, 3000);
      }

      return throwError(() => error);
    }),
  );
};

function shouldSkipUnauthorizedHandling(url: string): boolean {
  return SKIP_UNAUTHORIZED_PATHS.some((path) => url.includes(path));
}

function resolveLoginUrl(requestUrl: string, currentPath: string): string {
  if (requestUrl.includes('/admin/') || currentPath.startsWith('/admin')) {
    return '/admin-login';
  }

  return '/login';
}

function resolveForbiddenMessage(error: HttpErrorResponse): string {
  const message = error.error?.message;

  if (typeof message === 'string' && message.trim() !== '') {
    return message;
  }

  return 'You do not have permission to perform this action.';
}
